import React, { useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';

import { appColors } from 'config/colors-config';
import { FinecoButton } from 'pages/assistant/components/FinecoButton';
import { FinecoPaperClipIcon } from 'shared/assets/svg/FinecoPaperClipIcon';
import { IconNote } from 'shared/components/ui/IconNote';
import { createLogger } from 'shared/logger';
import type { ManualTableSeed } from 'shared/store/portfolioDocImportStore';
import { formatItalianNumber, formatItalianPercentageNo100 } from 'utils/formatUtils';
import {
  importPortfolioDocument,
  isAbortError,
  type ImportProgress,
  type ImportWarning,
  type PageOverview,
} from 'pages/assistant/services/portfolioDocumentImport';
import {
  canConfirm,
  computeTotals,
  defaultFigureMode,
  ERROR_MESSAGES,
  importErrorMessage,
  ISSUE_LABELS,
  MAX_PAGES,
  PORTFOLIO_DOC_ACCEPT,
  rowBlocker,
  rowCountText,
  sendableRows,
  STATUS_LABELS,
  toImportRows,
  toManualRows,
  validateDocument,
  valuesModeAllowed,
  WARNING_LABELS,
  formatPages,
  weightsTotalOk,
  type FigureMode,
  type ImportRow,
} from './portfolioImportRows';

import styles from './portfolioImport.module.scss';

const log = createLogger('PortfolioDocumentImport');

// The upload + confirmation table of the composer paperclip dialog (the only
// upload entry point). It never submits: «Usa questi dati» hands the rows to onUse.
//
// Accepted design (design/DESIGN_SPEC.md, «Importa da documento» simplified, November 2026): every step has one
// subtitle, the file line, one alert slot (document warnings and read errors) and one footer with the status on the
// left (page counter, progress bar, or why the button is off) and the one action on the right. Progress is a bar only.

type Review = { rows: ImportRow[]; mode: FigureMode; printedTotal: number | null; warnings: ImportWarning[] };
type PagePick = { overview: PageOverview[]; selected: number[]; warnings: ImportWarning[] };

type Props = {
  onUse: (seed: ManualTableSeed) => void;
};

const SUBTITLE_START = 'Carica il documento del portafoglio: PDF, JPEG, PNG, XLSX o CSV, fino a 20 MB.';
const SUBTITLE_REVIEW = 'Controlla ISIN e importi, poi usa i dati per l’analisi.';
const pagesSubtitle = (overview: PageOverview[]) =>
  overview.some((page) => page.isin_count > 0)
    ? `Documento di ${overview.length} pagine: scegli fino a ${MAX_PAGES} pagine. Abbiamo selezionato quelle con codici ISIN.`
    : `Documento di ${overview.length} pagine: scegli fino a ${MAX_PAGES} pagine, quelle con le posizioni del portafoglio.`;

// Colours from appColors, passed to the stylesheet as CSS variables.
const panelColors = {
  '--tile-blue': appColors.FinecoBluePrimary,
  '--tile-blue-light': appColors.BlueLight,
  '--tile-black': appColors.FinecoBlack,
  '--tile-gray': appColors.Gray1,
  '--tile-gray-faded': appColors.gray2,
  '--tile-gray-line': appColors.Gray3,
  '--tile-line': appColors.FinecoGrayLight1,
  '--tile-surface': appColors.FinecoGrayLight3,
  '--tile-white': appColors.FinecoWhite,
  '--tile-red': appColors.red,
  '--tile-orange': appColors.orange,
} as React.CSSProperties;

export const PortfolioDocumentImport: React.FC<Props> = ({ onUse }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [pagePick, setPagePick] = useState<PagePick | null>(null);
  const [review, setReview] = useState<Review | null>(null);

  const upload = async (document: File, pages?: string) => {
    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true);
    setProgress(null);
    setError(null);
    try {
      const result = await importPortfolioDocument(document, pages, {
        onProgress: setProgress,
        signal: controller.signal,
      });
      if (result.status === 'needs_pages') {
        const overview = result.page_overview ?? [];
        setReview(null);
        setPagePick({
          overview,
          selected: overview
            .filter((page) => page.isin_count > 0)
            .map((page) => page.page)
            .slice(0, MAX_PAGES),
          warnings: result.warnings,
        });
        return;
      }
      const rows = toImportRows(result.holdings);
      setPagePick(null);
      if (rows.length === 0) {
        setReview(null);
        setError(ERROR_MESSAGES.no_isin_found);
        return;
      }
      setReview({
        rows,
        mode: defaultFigureMode(rows),
        printedTotal: result.totals?.printed_total ?? null,
        warnings: result.warnings,
      });
    } catch (caught) {
      if (isAbortError(caught)) return; // the advisor cancelled: nothing to report
      // Content-free: no file name, no figures.
      log.error('portfolio document import failed', { status: (caught as { status?: number })?.status });
      setError(importErrorMessage(caught));
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setUploading(false);
      setProgress(null);
    }
  };

  const cancelUpload = () => abortRef.current?.abort();

  const handleFileSelected: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const selected = event.target.files?.[0];
    // Cleared so picking the same file again re-fires onChange.
    event.target.value = '';
    if (!selected) return;
    const invalid = validateDocument(selected);
    if (invalid) {
      setError(ERROR_MESSAGES[invalid]);
      return;
    }
    // A new document replaces whatever the previous one showed.
    setFile(selected);
    setPagePick(null);
    setReview(null);
    void upload(selected);
  };

  const updateRows = (update: (rows: ImportRow[]) => ImportRow[]) =>
    setReview((current) => current && { ...current, rows: update(current.rows) });
  const updateRow = (rowId: string, patch: Partial<ImportRow>) =>
    updateRows((rows) => rows.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));

  const togglePage = (page: number) =>
    setPagePick(
      (current) =>
        current && {
          ...current,
          selected: current.selected.includes(page)
            ? current.selected.filter((p) => p !== page)
            : [...current.selected, page],
        },
    );

  const pickFile = () => fileInputRef.current?.click();
  const progressBar = <ProgressBar progress={progress} />;
  const cancel = <FinecoButton variant="white" copy="Annulla" click={cancelUpload} testId="doc-upload-cancel" />;
  const warnings = (pagePick?.warnings ?? review?.warnings ?? []).filter((code) => code !== 'over_page_cap');

  return (
    <div className={styles.importPanel} style={panelColors}>
      <p className={styles.panelHint}>
        {pagePick ? pagesSubtitle(pagePick.overview) : review ? SUBTITLE_REVIEW : SUBTITLE_START}
      </p>

      {file && (
        <div className={styles.pickerRow}>
          <FinecoPaperClipIcon fill="#00549F" />
          <span className={styles.fileName}>{file.name}</span>
          <button
            type="button"
            className={styles.pageAction}
            disabled={uploading}
            onClick={pickFile}
            data-testid="doc-upload-pick"
          >
            Cambia documento
          </button>
        </div>
      )}
      <input
        type="file"
        ref={fileInputRef}
        accept={PORTFOLIO_DOC_ACCEPT}
        className={styles.hiddenInput}
        aria-label="Documento di portafoglio"
        data-testid="doc-upload-file-input"
        onChange={handleFileSelected}
      />

      <Alerts error={error} warnings={warnings} />

      {!file && (
        <button type="button" className={styles.pickerButton} onClick={pickFile} data-testid="doc-upload-pick">
          <FinecoPaperClipIcon fill="#00549F" />
          Scegli un documento
        </button>
      )}

      {pagePick && (
        <PagePicker
          pick={pagePick}
          reading={uploading}
          onToggle={togglePage}
          onClear={() => setPagePick((current) => current && { ...current, selected: [] })}
          onAnalyze={() => file && upload(file, formatPages(pagePick.selected))}
          readingStatus={progressBar}
          cancel={cancel}
        />
      )}

      {review && (
        <ConfirmationTable
          review={review}
          onRowChange={updateRow}
          onRemove={(rowId) => updateRows((rows) => rows.filter((row) => row.rowId !== rowId))}
          onModeChange={(mode) => setReview((current) => current && { ...current, mode })}
          onUse={() => onUse({ rows: toManualRows(review.rows, review.mode), byWeights: review.mode === 'weights' })}
        />
      )}

      {/* The first read: no picker or table yet, so the footer is the progress alone. */}
      {uploading && !pagePick && <Footer status={progressBar} action={cancel} testId="doc-upload-uploading" />}
    </div>
  );
};

// One footer for every step: the status on the left, the one action on the right.
const Footer: React.FC<{ status?: React.ReactNode; action: React.ReactNode; testId?: string }> = ({
  status,
  action,
  testId,
}) => (
  <div className={styles.pageFooter} data-testid={testId}>
    <div className={styles.footerStatus}>{status}</div>
    {action}
  </div>
);

// Progress is a bar only (no stage text, no timer): determinate in `figures`, the only stage with a real share
// (`isin` reports done = total), a sweeping segment in the other stages. The words are for screen readers.
const ProgressBar: React.FC<{ progress: ImportProgress | null }> = ({ progress }) => {
  const share =
    progress?.stage === 'figures' && progress.done != null && progress.total
      ? Math.min(progress.done / progress.total, 1)
      : null;
  return (
    <div
      className={share === null ? `${styles.progressBar} ${styles.progressBarIndeterminate}` : styles.progressBar}
      role="progressbar"
      aria-label="Lettura del documento"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={share === null ? undefined : Math.round(share * 100)}
      data-testid="doc-upload-progress"
    >
      <span style={share === null ? undefined : { width: `${Math.round(share * 100)}%` }} />
    </div>
  );
};

// The one alert slot: a read error (instead of a toast) and the document warnings, as Fineco notes. Codes this
// build does not know yet show no text.
const Alerts: React.FC<{ error: string | null; warnings: ImportWarning[] }> = ({ error, warnings }) => {
  const known = warnings.filter((code) => WARNING_LABELS[code]);
  if (!error && known.length === 0) return null;
  return (
    <div className={styles.notes} role="status" data-testid="doc-upload-alerts">
      {error && <IconNote type="generic-error" title="Documento non letto" body={error} />}
      {known.map((code) => (
        <IconNote key={code} type="attention" title="Attenzione" body={WARNING_LABELS[code]} />
      ))}
    </div>
  );
};

type PagePickerProps = {
  pick: PagePick;
  reading: boolean; // the chosen pages are being read: tiles locked, progress in the footer
  onToggle: (page: number) => void;
  onClear: () => void;
  onAnalyze: () => void;
  readingStatus: React.ReactNode;
  cancel: React.ReactNode;
};

const isinLabel = (count: number) => `${count} ISIN`;

// Pages with ISINs are large tiles (number + ISIN count), the others compact faded tiles that still toggle. The
// tiles are the selection; «Deseleziona tutte» beside the counter is the only other control.
const PagePicker: React.FC<PagePickerProps> = ({ pick, reading, onToggle, onClear, onAnalyze, readingStatus, cancel }) => {
  const count = pick.selected.length;
  const tooMany = count > MAX_PAGES;
  return (
    <div className={styles.pagePicker} data-testid="doc-upload-page-picker">
      <fieldset
        className={reading ? `${styles.pageLock} ${styles.pageLocked}` : styles.pageLock}
        disabled={reading}
        aria-busy={reading}
      >
        <ul className={styles.pageTiles}>
          {pick.overview.map((page) => {
            const on = pick.selected.includes(page.page);
            const hasIsin = page.isin_count > 0;
            return (
              <li key={page.page}>
                <button
                  type="button"
                  className={[
                    styles.pageTile,
                    hasIsin ? styles.pageTileIsin : styles.pageTileCompact,
                    on && styles.pageTileOn,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={on}
                  aria-label={`Pagina ${page.page}, ${hasIsin ? isinLabel(page.isin_count) : 'nessun ISIN'}`}
                  data-testid={`doc-upload-page-${page.page}`}
                  onClick={() => onToggle(page.page)}
                >
                  <span className={styles.pageTileNumber}>{page.page}</span>
                  {hasIsin && <span className={styles.pageTileMeta}>{isinLabel(page.isin_count)}</span>}
                  {hasIsin && on && (
                    <span className={styles.pageTileCheck} aria-hidden="true">
                      ✓
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </fieldset>
      {reading ? (
        <Footer status={readingStatus} action={cancel} testId="doc-upload-reading-footer" />
      ) : (
        <Footer
          status={
            <span className={styles.pageCounterLine}>
              <span
                className={tooMany ? styles.pageCounterOver : styles.pageCounter}
                role="status"
                data-testid="doc-upload-page-counter"
              >
                {tooMany
                  ? `${count} pagine selezionate: il massimo è ${MAX_PAGES}`
                  : `${count} di ${MAX_PAGES} pagine selezionate`}
              </span>
              {count > 0 && (
                <button
                  type="button"
                  className={styles.pageAction}
                  onClick={onClear}
                  data-testid="doc-upload-select-none"
                >
                  Deseleziona tutte
                </button>
              )}
            </span>
          }
          action={
            <FinecoButton
              click={onAnalyze}
              copy="Analizza le pagine selezionate"
              testId="doc-upload-analyze-pages"
              disabled={count === 0 || tooMany}
            />
          }
        />
      )}
    </div>
  );
};

type FigureTone = 'invalid' | 'check';

type FigureInputProps = {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  decimalScale?: number;
  tone?: FigureTone;
};

const TONE_CLASS: Record<FigureTone, string> = { invalid: styles.cellInvalid, check: styles.cellCheck };

// tone: `invalid` blocks «Usa questi dati» (red, «Inserisci»); `check` was read as missing and is worth a look.
const FigureInput: React.FC<FigureInputProps> = ({ value, onChange, ariaLabel, disabled, decimalScale = 2, tone }) => (
  <NumericFormat
    value={value}
    valueIsNumericString
    thousandSeparator="."
    decimalSeparator=","
    allowedDecimalSeparators={[',', '.']}
    decimalScale={decimalScale}
    allowNegative={false}
    inputMode="decimal"
    aria-label={ariaLabel}
    aria-invalid={tone === 'invalid' || undefined}
    placeholder={tone === 'invalid' ? 'Inserisci' : undefined}
    className={tone ? `${styles.figureInput} ${TONE_CLASS[tone]}` : styles.figureInput}
    disabled={disabled}
    onValueChange={(values) => onChange(values.value)}
  />
);

const STATUS_CLASS = {
  ok: styles.badgeOk,
  check_figures: styles.badgeCheck,
  isin_repaired: styles.badgeRepaired,
  out_of_catalog: styles.badgeOut,
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

type ConfirmationTableProps = {
  review: Review;
  onRowChange: (rowId: string, patch: Partial<ImportRow>) => void;
  onRemove: (rowId: string) => void;
  onModeChange: (mode: FigureMode) => void;
  onUse: () => void;
};

const ConfirmationTable: React.FC<ConfirmationTableProps> = ({ review, onRowChange, onRemove, onModeChange, onUse }) => {
  const { rows, mode, printedTotal } = review;
  const totals = computeTotals(rows);
  const sendable = sendableRows(rows);
  const excludedCount = rows.length - sendable.length;
  const valuesAllowed = valuesModeAllowed(rows);
  const weightsOff = mode === 'weights' && !weightsTotalOk(rows);
  // Each printed value is rounded to the cent: allow half a cent per row before flagging the printed total.
  const printedMismatch =
    printedTotal !== null && Math.abs(totals.documentValueSum - printedTotal) > 0.005 * rows.length + 1e-9;
  // Why «Usa questi dati» is off, beside it in the footer.
  const reason =
    sendable.length === 0
      ? 'Nessuno strumento da analizzare.'
      : sendable.some((row) => rowBlocker(row, mode) !== null)
        ? 'Completa le celle evidenziate.'
        : weightsOff
          ? `I pesi devono totalizzare 100%: ora ${formatItalianPercentageNo100(totals.sentWeightSum)}.`
          : null;

  return (
    <>
      <div className={styles.tableBar}>
        {valuesAllowed ? (
          <div className={styles.modeSwitch} role="radiogroup" aria-label="Analizza per">
            <span className={styles.modeLabel}>Analizza per</span>
            {(['weights', 'values'] as FigureMode[]).map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={mode === option}
                data-testid={`doc-upload-mode-${option}`}
                onClick={() => onModeChange(option)}
              >
                {option === 'weights' ? 'Peso' : 'Controvalore'}
              </button>
            ))}
          </div>
        ) : (
          <span className={styles.muted}>Valute diverse: l’analisi usa i pesi.</span>
        )}
        <span className={styles.muted} data-testid="doc-upload-row-count">
          {rowCountText(sendable.length, excludedCount)}
        </span>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table} data-testid="doc-upload-table">
          <thead>
            <tr>
              <th>ISIN</th>
              <th>Descrizione</th>
              <th>Valuta</th>
              <th className={styles.colFigure}>Quantità</th>
              <th className={styles.colFigure}>Controvalore</th>
              <th className={styles.colFigure}>Peso %</th>
              <th aria-label="Rimuovi" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const blocker = rowBlocker(row, mode);
              const readOnly = !row.inCatalog;
              const modeField = mode === 'weights' ? 'weight' : 'value';
              // Only the column the analysis uses is ever highlighted: an empty Peso is fine «per Controvalore».
              const figureTone = (field: 'value' | 'weight'): FigureTone | undefined => {
                if (readOnly || field !== modeField) return undefined;
                if (blocker && blocker !== 'ISIN non valido') return 'invalid';
                return row[field] === '' ? 'check' : undefined;
              };
              // Codes this build does not know yet show no text.
              const tip = row.issues
                .filter((issue) => ISSUE_LABELS[issue])
                .map((issue) => capitalize(ISSUE_LABELS[issue]))
                .join('. ');
              return (
                <tr
                  key={row.rowId}
                  className={row.inCatalog ? undefined : styles.excludedRow}
                  data-testid="doc-upload-row"
                  data-row-id={row.rowId}
                  data-status={row.status}
                >
                  <td>
                    <input
                      className={
                        blocker === 'ISIN non valido' ? `${styles.isinInput} ${styles.cellInvalid}` : styles.isinInput
                      }
                      value={row.isin}
                      aria-label={`ISIN riga ${row.rowId}`}
                      aria-invalid={blocker === 'ISIN non valido' || undefined}
                      maxLength={12}
                      disabled={readOnly}
                      onChange={(e) => onRowChange(row.rowId, { isin: e.target.value.toUpperCase().trim() })}
                    />
                    {row.status !== 'ok' && (
                      <span
                        className={`${styles.badge} ${STATUS_CLASS[row.status]}`}
                        title={tip || undefined}
                        data-testid="doc-upload-status-badge"
                        data-status={row.status}
                      >
                        {STATUS_LABELS[row.status]}
                        {tip && <span className="sr-only">: {tip}</span>}
                      </span>
                    )}
                  </td>
                  <td className={styles.colDescription}>{row.description ?? '—'}</td>
                  <td>{row.currency ?? '—'}</td>
                  <td className={styles.colFigure}>
                    <FigureInput
                      value={row.quantity}
                      onChange={(quantity) => onRowChange(row.rowId, { quantity })}
                      ariaLabel={`Quantità ${row.isin}`}
                      disabled={readOnly}
                      decimalScale={4}
                    />
                  </td>
                  <td className={styles.colFigure}>
                    <FigureInput
                      value={row.value}
                      onChange={(value) => onRowChange(row.rowId, { value })}
                      ariaLabel={`Controvalore ${row.isin}`}
                      disabled={readOnly}
                      tone={figureTone('value')}
                    />
                  </td>
                  <td className={styles.colFigure}>
                    <FigureInput
                      value={row.weight}
                      onChange={(weight) => onRowChange(row.rowId, { weight })}
                      ariaLabel={`Peso % ${row.isin}`}
                      disabled={readOnly}
                      tone={figureTone('weight')}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => onRemove(row.rowId)}
                      aria-label={`Rimuovi ${row.isin}`}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className={styles.totalLabel}>
                Totale
              </td>
              <td className={`${styles.colFigure} ${styles.total}`} data-testid="doc-upload-total-value">
                {formatItalianNumber(totals.documentValueSum)}
                {printedMismatch && (
                  <span className={styles.totalNote}>nel documento {formatItalianNumber(printedTotal)}</span>
                )}
              </td>
              <td
                className={`${styles.colFigure} ${styles.total} ${weightsOff ? styles.totalOff : ''}`}
                data-testid="doc-upload-total-weight"
              >
                {mode === 'weights' && formatItalianPercentageNo100(totals.sentWeightSum)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <Footer
        status={
          reason && (
            <span className={styles.footerReason} data-testid="doc-upload-reason">
              {reason}
            </span>
          )
        }
        action={
          <FinecoButton click={onUse} copy="Usa questi dati" testId="doc-upload-confirm" disabled={!canConfirm(rows, mode)} />
        }
      />
    </>
  );
};
